import { Alert, Badge, Loader, Stack, Table, Text, Title } from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { TrustRuleDecision } from "./api";
import { useTrustRuleApplications } from "./useTrustRuleApplications";

// Цвет badge по решению: applied — зелёный (правило сработало),
// rejected — красный (файлы матчатся, но действие не одобрено),
// not_matched — серый (файлы вне паттернов).
const DECISION_COLOR: Record<TrustRuleDecision, string> = {
  applied: "green",
  rejected: "red",
  not_matched: "gray",
};

// Read-only история применений trust rules. Записи пишет
// FSM-сервис при каждом решении; здесь только показываем.
export function TrustRuleApplicationsSection(): ReactElement {
  const { t } = useTranslation();
  const applications = useTrustRuleApplications();

  if (applications.isPending) {
    return <Loader />;
  }

  if (applications.isError) {
    return (
      <Stack gap="md">
        <Title order={2}>{t("trustRules.history.title")}</Title>
        <Alert color="red" title={t("trustRules.errorHeading")}>
          {t("trustRules.loadingError")}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Title order={2}>{t("trustRules.history.title")}</Title>
      {applications.data.length === 0 ? (
        <Text c="dimmed">{t("trustRules.history.empty")}</Text>
      ) : (
        <Table withTableBorder withColumnBorders striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("trustRules.history.colRule")}</Table.Th>
              <Table.Th>{t("trustRules.history.colTask")}</Table.Th>
              <Table.Th>{t("trustRules.history.colDecision")}</Table.Th>
              <Table.Th>{t("trustRules.history.colAt")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {applications.data.map((application) => (
              <Table.Tr key={application.id}>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {application.trust_rule_id.slice(0, 8)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {application.task_id.slice(0, 8)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={DECISION_COLOR[application.decision]}>
                    {t(`trustRules.decision.${application.decision}`)}
                  </Badge>
                </Table.Td>
                <Table.Td>{application.applied_at}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
